import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { isResponseWithData } from 'lib-shared-auth';
import { FeatureFlags } from 'lib-shared-feature-flags';
import { AsyncStatus } from 'lib-shared-types-api';
import type {
  DeleteAppLoginApiArg,
  DeleteAppLoginApiResponse,
  Organization,
} from 'lib-shared-types-api';

import { loginApi } from 'apis/login';
import { organizationStorageApi } from 'apis/organizationStorage';
import { userStorageApi } from 'apis/userStorage';
import { createAsyncThunkAction } from 'utils/redux/createAsyncThunkAction';

import { AppError, AppLogInType } from './types/app.types';
import {
  ConversationTransferRequest,
  ConversationTransferRequestFulfilled,
  ConversationTransferRequestRejected,
} from './types/appConversationTransfer.types';
import { AppModal } from './types/appModal.types';
import { createShowModalAction } from './utils/createShowModalAction';

const prefix = 'app';

export const appActions = {
  logIn: createAction<{ type: AppLogInType }>(`${prefix}/logIn`),
  logOut: createAsyncThunk<DeleteAppLoginApiResponse, DeleteAppLoginApiArg>(
    `${prefix}/logOut`,
    async (arg, { dispatch, rejectWithValue }) => {
      const response = await dispatch(
        loginApi.endpoints.deleteAppLogin.initiate({
          refreshToken: arg.refreshToken,
        }),
      );

      return isResponseWithData(response)
        ? response.data
        : rejectWithValue(response.error);
    },
  ),
  setError: createAction<{ error: AppError }>(`${prefix}/error`),
  setStatus: createAction<{ status: AsyncStatus }>(`${prefix}/status`),
  showModal: createShowModalAction(`${prefix}/showModal`),
  hideModal: createAction<Pick<AppModal['modal'], 'type'>>(
    `${prefix}/hideModal`,
  ),
  getFeatureFlags: createAsyncThunkAction<void, { flags: FeatureFlags }>(
    `${prefix}/getFeatureFlags`,
  ),
  getPresence: createAsyncThunkAction(`${prefix}/getPresence`),
  getServerStorage: createAsyncThunk(
    `${prefix}/getServerStorage`,
    async (_arg, { dispatch, rejectWithValue }) => {
      const [organizationStorageResponse, userStorageResponse] =
        await Promise.all([
          dispatch(
            organizationStorageApi.endpoints.readOrganizationStorage.initiate(
              {},
            ),
          ),
          dispatch(userStorageApi.endpoints.readUserStorage.initiate({})),
        ]);

      if (
        !isResponseWithData(organizationStorageResponse) ||
        !isResponseWithData(userStorageResponse)
      ) {
        return rejectWithValue({
          organizationStorageError: organizationStorageResponse.error,
          userStorageError: userStorageResponse.error,
        });
      }
    },
  ),
  conversationTransferRequest: createAsyncThunkAction<
    ConversationTransferRequest,
    ConversationTransferRequestFulfilled,
    ConversationTransferRequestRejected
  >(`${prefix}/conversationTransferRequest`),
  conversationTransferClear: createAction<{ csid: number }>(
    `${prefix}/conversationTransferClear`,
  ),
  setOrganization: createAction<{
    organization: Organization;
  }>(`${prefix}/setOrganization`),
  setOrganizations: createAction<{
    organizations: Organization[];
  }>(`${prefix}/setOrganizations`),
};
